import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpInterceptorFn,
  HttpRequest,
} from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { catchError, Observable, throwError } from 'rxjs';
import { FrontendType } from '../enums/frontend-type.enum';
import { HttpCustomHeader } from '../enums/http-custom-header.enum';
import { AuthenticationService } from '../services/authentication/authentication.service';
import { PlatformService } from '../services/platform/platform.service';
import { APP_HTTP_HOOKS } from './app-http-hooks';
import { StringService } from '../utils/string/string.service';
import { InitiatorPlatform } from '../enums/initiator-platform.enum';

// HTTP 426 Upgrade Required — emitted by the API's MinVersionInterceptor for a
// sub-minimum mobile build. One-off here, so a local constant (not an enum).
const UPGRADE_REQUIRED_STATUS = 426;

// The only routes the API accepts a half-signed-in (MFA-pending) token on: see
// MFARoutes in the API's AccessControlGuard. Everything else refuses it with 403.
const MFA_PENDING_TOKEN_URLS = [
  '/authentication/mfa/verify',
  '/authentication/mfa/request-reset',
  '/authentication/mfa/initiate-registration',
  '/authentication/mfa/confirm-registration',
];

@Injectable({
  providedIn: 'root',
})
export class AuthenticationInterceptorService implements HttpInterceptor {
  private readonly authenticationService = inject(AuthenticationService);
  private readonly stringService = inject(StringService);
  private readonly platformService = inject(PlatformService);
  /** App-specific request headers and the 426 handler; absent in admin. */
  private readonly httpHooks = inject(APP_HTTP_HOOKS, { optional: true });

  intercept(
    req: HttpRequest<unknown>,
    next: HttpHandler,
  ): Observable<HttpEvent<unknown>> {
    // Add headers for all requests
    const authReq = this.addAuthHeaders(
      req,
      this.tokenFor(req.url),
      this.platformService.getBaseHref(),
      this.platformService.getInitiatorPlatform(),
    );

    // Send the request and handle errors
    return next.handle(authReq).pipe(
      catchError((err: HttpErrorResponse) => {
        // Honour the API's 426 Upgrade Required backstop (MinVersionInterceptor):
        // any call from a sub-minimum mobile build is rejected with 426. Surface
        // the blocking upgrade screen immediately so gating doesn't wait for the
        // next launch/resume version-check (covers a policy that tightened
        // mid-session). The body carries the server's upgrade message.
        if (err.status === UPGRADE_REQUIRED_STATUS) {
          const body = err.error as { message?: string } | null;
          this.httpHooks?.onUpgradeRequired?.(body?.message ?? null);
          return throwError(() => err);
        }

        return throwError(() => err);
      }),
    );
  }

  private addAuthHeaders(
    req: HttpRequest<unknown>,
    token: string | null,
    frontendType: FrontendType,
    initiatorPlatform: InitiatorPlatform,
  ): HttpRequest<unknown> {
    let headers = req.headers
      .set(HttpCustomHeader.FRONTEND_TYPE, frontendType)
      .set(HttpCustomHeader.INITIATOR_PLATFORM, initiatorPlatform);
    // Whatever this app adds to every request — for the user app, its
    // platform and build number, so the API's 426 backstop can catch a
    // sub-minimum build on any call and not just the version-check endpoint.
    // Contributed through a token so this shared interceptor does not have to
    // know which app it is running in.
    const extra = this.httpHooks?.requestHeaders?.() ?? null;
    if (extra) {
      for (const [name, value] of Object.entries(extra)) {
        headers = headers.set(name, value);
      }
    }
    if (token) {
      const bearerToken = this.stringService.getBearerToken(token);
      if (bearerToken) {
        headers = headers.set(
          HttpCustomHeader.ACCESS_TOKEN_HEADER,
          bearerToken,
        );
      }
    }

    return req.clone({
      headers,
    });
  }

  /**
   * The access token to send with this request, or null to send it as a guest.
   *
   * A session that has passed the password step but still owes MFA holds a
   * token the API honours on the MFA routes ONLY. Elsewhere the API now judges
   * it as no token (ADR-0302), but an API before that refused it with 403 -
   * public routes included. Until MFA completes the user is still a guest,
   * so everything else goes out as one: the version check, the country check
   * and the catalog behind "Go to home" answer instead of failing with a toast.
   */
  private tokenFor(url: string): string | null {
    if (this.isAuthUrl(url)) return null;
    if (
      this.authenticationService.hasMfaPendingSession() &&
      !this.isMfaPendingTokenUrl(url)
    ) {
      return null;
    }
    return this.authenticationService.getAccessToken();
  }

  private isMfaPendingTokenUrl(url: string): boolean {
    const path = url.split('?')[0];
    return MFA_PENDING_TOKEN_URLS.some((mfaUrl) => path.endsWith(mfaUrl));
  }

  private isAuthUrl(url: string): boolean {
    // Public auth endpoints where we must NOT forward the stored access
    // token. If we did, a stale token (e.g. one with mfaRequired=true left
    // over from an abandoned MFA flow) would be sent on the new login
    // attempt, and the backend's AccessControlGuard would reject the
    // re-login with 403 because mfaRequired blocks every route except
    // /authentication/mfa/verify.
    return (
      url.endsWith('/authentication/login') ||
      url.endsWith('/authentication/admin/login') ||
      url.endsWith('/authentication/refresh-token') ||
      url.endsWith('/authentication/request-password-reset') ||
      url.endsWith('/authentication/reset-password') ||
      url.endsWith('/authentication/register') ||
      url.endsWith('/authentication/register-organization') ||
      url.endsWith('/authentication/verify-email') ||
      url.endsWith('/authentication/oauth') ||
      url.endsWith('/authentication/oauth/bridge-exchange') ||
      url.endsWith('/authentication/oauth/nonce') ||
      url.endsWith('/authentication/request-email-verification') ||
      url.endsWith('/authentication/verify-email-change') ||
      url.endsWith('/authentication/mfa/disable-with-token') ||
      url.endsWith('/authentication/logout')
    );
  }
}

export const authenticationInterceptor: HttpInterceptorFn = (req, next) => {
  const interceptor = inject(AuthenticationInterceptorService);
  const nextHandler: HttpHandler = {
    handle: (request: HttpRequest<any>) => next(request),
  };
  return interceptor.intercept(req, nextHandler);
};
